import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import SearchButton from 'components/form/SearchButton'
import CourseBasicData from 'components/form/course/CourseBasicData'
import { ClientRoute } from 'routes/client'
import AddProviderMapping from './CourseAddProviderMapping'
import { TableBody, TableCell, TableRow } from '@mui/material'
import { useDispatch } from 'react-redux'
import { updateCourseProviderMapping } from 'store/course/thunks'
import { COURSE_PROVIDER } from 'config'

interface Props {
  course: Course
  clubId: number | null
  toggleClubModal: () => void
}

export default function CourseProviderMapping({ course, clubId, toggleClubModal }: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()

  const handleUnmapping = () => {
    if (!course.providerCourseMap?.id) {
      return
    }
    dispatch(
      updateCourseProviderMapping({
        courseId: course.id,
        courseProviderMapId: course.providerCourseMap?.id,
        newProvider: COURSE_PROVIDER.GGB,
        newProviderId: course.id.toString(),
      }),
    )
  }

  const shouldDisableButton = () => {
    return course.provider?.abbreviation === COURSE_PROVIDER.GGB
  }

  return (
    <PageContainer>
      <SectionContainer>
        <CourseBasicData course={course} clubId={clubId} />
        <SectionContainer>
          <FormTable>
            <TableBody>
              <TableRow>
                <TableCell sx={{ borderBottom: '0px', fontWeight: 'inherit', color: 'inherit', fontSize: 'inherit' }}>
                  {intl.formatMessage({ id: 'strings.providerCourseId' })}
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: '0px',
                    fontWeight: 'inherit',
                    color: 'inherit',
                    fontSize: 'inherit',
                  }}
                >
                  {course.providerCourseMap?.providerCourseId}
                </TableCell>
              </TableRow>
            </TableBody>
          </FormTable>
          <SectionContainer>
            <UpdateButton onClick={handleUnmapping} text={'Unmap'} disabled={shouldDisableButton()} />
          </SectionContainer>
        </SectionContainer>
      </SectionContainer>
      <SectionContainer>
        <SectionTitle>Search Course For Mapping</SectionTitle>
        <AddProviderMapping />
      </SectionContainer>
      <SectionContainer>
        <CourseActionButtons>
          <CreateCourseVersion
            onClick={() => null}
            text={intl.formatMessage({ id: 'strings.createCourseVersion' })}
            status="idle"
            disabled
          />
          <ActionButton onClick={toggleClubModal}>{intl.formatMessage({ id: 'strings.editClub' })}</ActionButton>
          {course.club?.id && (
            <LinkButton to={ClientRoute.ADMIN_CLUB_COURSES(course.club.id)}>
              {intl.formatMessage({ id: 'strings.backToCourses' })}
            </LinkButton>
          )}
        </CourseActionButtons>
      </SectionContainer>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  margin: 0 10px;
  padding: 0 0 20px 0;
  min-height: 500px;

  & a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
  }
`

const SectionContainer = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  margin: 0 0 10px;
  padding: 20px;
`

const CourseActionButtons = styled.div`
  display: flex;
  & > * {
    margin-right: 30px;
  }
`

const CreateCourseVersion = styled(SearchButton)``

const LinkButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 180px;
  height: 50px;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  font-weight: 500;
  font-size: 0.9em;
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }
`

const ActionButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 180px;
  height: 54px;
  background-color: transparent;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  font-weight: 500;
  font-size: 0.9em;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`

const SectionTitle = styled.h2`
  width: 100%;
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const FormTable = styled.table`
  width: 100%;
  & tr {
    height: 40px;
    & td {
      letter-spacing: 0.4px;
      vertical-align: bottom;
      padding: 5px 0;

      &:first-of-type {
        padding-right: 20px;
        text-align: right;
        width: 100px;
      }

      &.disabled {
        opacity: 0.5;
      }
    }
  }
`

const UpdateButton = styled(SearchButton)`
  &.Mui-disabled {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    opacity: 0.5;
  }
  text-transform: none;
`
